import { type DebouncedFunc, throttle } from 'lodash';
import ms from 'ms';
import { ContainerInstance, Service } from 'typedi';
import { ContainerTokens } from '../constants';
import { EncryptionService } from '../services/encryption';
import { COMMIT_HASH } from '../vite/version';
import {
  type PersistentState,
  type PersistentStorageEngine,
} from './persistent-storage-engine';
import { type RootState } from './root.state';

export const PERSISTENT_STORE_VERSION = COMMIT_HASH;
export const PERSISTENT_STORE_KEY = 'prayerstream-store';
export const PERSISTENT_STORE_THROTTLE_DELAY = ms('1s');

export type PersistFunction = (state: PersistentState) => Promise<void>;

@Service()
export class PersistStoreService {
  private readonly encryptionService: EncryptionService;

  private readonly persistentStorageEngine: PersistentStorageEngine;

  private readonly throttledSetState: DebouncedFunc<PersistFunction>;

  constructor(container: ContainerInstance) {
    this.persistentStorageEngine = container.get<PersistentStorageEngine>(
      ContainerTokens.PERSISTENT_STORAGE_ENGINE,
    );

    this.encryptionService = container.get(EncryptionService);

    this.throttledSetState = throttle<PersistFunction>(async (state) => {
      try {
        const encryptedState = this.encryptionService.encryptObject(state);

        await this.persistentStorageEngine.setItem<string>(
          PERSISTENT_STORE_KEY,
          encryptedState,
        );
      } catch {
        // do nothing
      }
    }, PERSISTENT_STORE_THROTTLE_DELAY);
  }

  async loadState(): Promise<Partial<RootState> | undefined> {
    try {
      const encryptedState =
        await this.persistentStorageEngine.getItem<string>(
          PERSISTENT_STORE_KEY,
        );

      if (!encryptedState) {
        return undefined;
      }

      const decryptedState = this.encryptionService.decryptObject(
        encryptedState,
      ) as PersistentState;
      if (decryptedState.version !== PERSISTENT_STORE_VERSION) {
        await this.persistentStorageEngine.clear();
        return undefined;
      }
      return decryptedState.state;
    } catch {
      await this.persistentStorageEngine.clear();
      return undefined;
    }
  }

  async saveState(state: Partial<RootState>): Promise<void> {
    const storedState: PersistentState = {
      state,
      version: PERSISTENT_STORE_VERSION,
    };

    return this.throttledSetState(storedState);
  }
}
